import React, { useContext, useState } from "react";

import styled from "@emotion/styled";

import { DataContext } from "../../../../../providers/PelletsDataProvider";
import pushToAnalytics from "../../../../../utils/analytics";
import NewInput from "../../../../shared/forms/NewInput";
import NewSelect from "../../../../shared/forms/NewSelect";
import LoadingIndicator from "../../../../shared/LoadingIndicator";
import Button from "../../../../shared/NewForms/Button";
import ProductSelect from "./ProductSelect";
import ZipcodeInput from "./ZipcodeInput";

const CalculatorContainer = styled.div`
    display: flex;
    justify-content: center;
`;

const Form = styled.form`
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 20px;
    gap: 6px 12px;

    ${({ theme }) => theme.breakpoints.from.small.css`
        justify-content: space-between;
    `}
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 20px;
    margin-bottom: 30px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 48%;

    ${({ theme }) => theme.breakpoints.from.small.css`
        width: 30%;
    `}
`;

const Calculator = () => {
    const dataContext = useContext(DataContext);
    const [shouldCalculate, setShouldCalculate] = useState(false);
    const {
        product,
        zipcode,
        amount,
        zipcodeError,
        zipcodeIsValid,
        amountError,
        isCalculatorReady,
    } = dataContext.data;
    const {
        setProduct,
        setZipcode,
        setAmount,
        calculateLoose,
        calculatePalletBags,
        calculatePalletBigBags,
        setZipcodeError,
        setAmountError,
        setResult,
        setAdditions,
    } = dataContext.actions;

    const [localZipcode, setLocalZipcode] = useState(zipcode);
    const [localProduct, setLocalProduct] = useState(product);
    const [localAmount, setLocalAmount] = useState(amount);

    const onChangeAmount = (event) => {
        setLocalAmount(event.target.value);
        setShouldCalculate(true);
    };

    const onSubmit = (event) => {
        event.preventDefault();

        if (
            localZipcode.length === 5 &&
            zipcodeIsValid &&
            !zipcodeError &&
            !amountError
        ) {
            setZipcode(localZipcode);
            setAmount(localAmount);
            setProduct(localProduct);
            setShouldCalculate(false);
            // Trigger a gtm event
            pushToAnalytics("pel:calculate", {
                zipcode: localZipcode,
                amount: localAmount,
                product: localProduct,
            });

            if (localProduct === "loose") {
                calculateLoose(localZipcode, localAmount);
            } else if (localProduct === "bags") {
                calculatePalletBags(localZipcode, localAmount);
            } else if (localProduct === "big-bag") {
                calculatePalletBigBags(localZipcode, localAmount);
            }
        } else {
            setResult([]);
            setAdditions(null);
        }
    };

    if (!isCalculatorReady) {
        return <LoadingIndicator />;
    }

    return (
        <CalculatorContainer>
            <Form onSubmit={onSubmit}>
                <InputWrapper>
                    <ZipcodeInput
                        setLocalZipcode={setLocalZipcode}
                        localProduct={localProduct}
                        setShouldCalculate={setShouldCalculate}
                        localZipcode={localZipcode}
                        setZipcodeError={setZipcodeError}
                    />
                </InputWrapper>
                <InputWrapper>
                    <ProductSelect
                        localProduct={localProduct}
                        setLocalAmount={setLocalAmount}
                        setLocalProduct={setLocalProduct}
                        setShouldCalculate={setShouldCalculate}
                    />
                </InputWrapper>
                <InputWrapper>
                    <AmountInput
                        product={localProduct}
                        amount={localAmount}
                        onChange={onChangeAmount}
                        hasError={amountError}
                        onError={() => setAmountError(true)}
                        onErrorSolved={() => {
                            setAmountError(false);
                        }}
                    />
                </InputWrapper>
                <ButtonWrapper>
                    <Button
                        type="submit"
                        data-gtag="pel-calculate-advanced"
                        color={shouldCalculate ? "red" : "redOutline"}
                    >
                        Neu berechnen
                    </Button>
                </ButtonWrapper>
            </Form>
        </CalculatorContainer>
    );
};

/**
 * Renders the corresponding amount input.
 */
export const AmountInput = ({
    product,
    amount,
    onChange,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError = () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onErrorSolved = () => {},
    hasError = false,
}) => {
    const placeholder =
        product === "loose"
            ? "Menge in kg:"
            : product === "big-bag"
            ? "Menge Säcke:"
            : "Paletten:";
    if (product === "loose") {
        return (
            <NewInput
                name="pelLooseAmount"
                key="loose-input"
                placeholder={placeholder}
                value={amount}
                onChange={onChange}
                validation="pel:pelLooseAmount"
                onError={onError}
                onErrorSolved={onErrorSolved}
                forceError={hasError}
                required
                fullWidth
            />
        );
    }

    const options = [
        { value: 1, text: "1" },
        { value: 2, text: "2" },
        { value: 3, text: "3" },
        { value: 4, text: "4" },
        { value: 5, text: "5" },
        { value: 6, text: "6" },
    ];

    return (
        <NewSelect
            name="amount"
            placeholder={placeholder}
            options={options}
            value={amount}
            onChange={onChange}
            minWidth="120px"
        />
    );
};

export default Calculator;
